import classNames from 'classnames';
import { FC, useRef } from 'react';
import { SubmitHandler, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import Avatar from 'src/components/ui/Avatar/Avatar';
import { FormInput } from 'src/components/ui/FormInput/FormInput';
import { FormPhoneInput } from 'src/components/ui/FormPhoneInput/FormPhoneInput';
import { FormRadio } from 'src/components/ui/FormRadio/FormRadio';
import { HTMLCountrySelectElement } from 'src/components/ui/PhoneInput/PhoneInput';
import { formRules, maxLengthRules } from 'src/constants/formConstants';
import { addCompany, editCompany } from 'src/store/actions/companyActions';
import {
  addManufacturerAccount,
  editManufacturerAccount,
  editManufacturerAccountEmail,
} from 'src/store/actions/manufacturerAccountActions';
import { Company } from 'src/store/models/Company';
import {
  ManufacturerAccount,
  ManufacturerEntity,
} from 'src/store/models/ManufacturerAccount';
import { AccountStatus } from 'src/store/models/ServiceAccount';
import countries from 'src/utils/countries';
import { useCountryPhoneSelect } from 'src/utils/useCountryPhoneSelect';
import { useErrorSelector } from 'src/utils/useLoadingSelector';

import classes from '../ManufacturerAccountDetails.module.scss';
import ManufacturerAccountDetailsFormSkeleton from './ManufacturerAccountDetailsFormSkeleton';

export interface ManufacturerAccountDetailsFormData
  extends Omit<ManufacturerAccount, 'enabled' | 'manufacturer'> {
  enabled: AccountStatus;
  manufacturer: Omit<ManufacturerEntity, 'appNames'> & { appNames: string };
}

export interface ManufacturerAccountDetailsFormProps {
  loading?: boolean;
  edit?: boolean;
}

const ManufacturerAccountDetailsForm: FC<
  ManufacturerAccountDetailsFormProps
> = ({ loading = false, edit = false }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const phoneSelectRef = useRef<HTMLCountrySelectElement[]>([]);

  const manufacturerAccountDetails = useAppSelector(
    (state) => state.manufacturerAccount.manufacturerAccountDetails
  );

  const manufacturerError = useErrorSelector<ManufacturerEntity>({
    actionType: edit
      ? editManufacturerAccount.type
      : addManufacturerAccount.type,
  });
  const manufacturerEmailError = useErrorSelector({
    actionType: editManufacturerAccountEmail.type,
  });

  const companyError = useErrorSelector<Company>({
    actionType: edit ? editCompany.type : addCompany?.type,
  });

  const { control, watch, getValues, handleSubmit, reset } =
    useFormContext<ManufacturerAccountDetailsFormData>();
  const notes = watch('manufacturer.baseCompany.notes');
  const country = watch('manufacturer.baseCompany.country');

  useCountryPhoneSelect(country, phoneSelectRef?.current);

  const onSubmit: SubmitHandler<ManufacturerAccountDetailsFormData> = (
    formData
  ) => {
    if (edit) {
      reset(formData);
      dispatch(
        editManufacturerAccount({
          ...formData,
          eMail:
            formData?.manufacturer?.baseCompany?.eMail !==
            manufacturerAccountDetails?.manufacturer?.baseCompany?.eMail
              ? formData?.manufacturer?.baseCompany?.eMail
              : '',
        })
      );
    } else {
      dispatch(addManufacturerAccount(formData));
    }
  };

  return (
    <form
      noValidate
      id="manufacturer-account-form"
      className={classNames({ [classes.Loading]: loading })}
      onSubmit={handleSubmit(onSubmit)}
    >
      {!loading ? (
        <>
          <h4 className={classNames({ [classes.Edit]: edit })}>
            {t('service_account:company_info')}
          </h4>
          {edit && (
            <Avatar
              className={classes.Avatar}
              showTitles={false}
              imageSize={96}
              title={getValues()?.manufacturer?.baseCompany?.title}
              image={manufacturerAccountDetails?.image}
            />
          )}
          <section>
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.title"
              placeholder={t('service_account:enter_company_name')}
              label={t('service_account:company_name')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!companyError?.title}
              helperText={companyError?.title}
            />
            <div />
            <div />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.line1"
              placeholder={t('common:enter_street_name')}
              label={t('common:address_line_1')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!companyError?.line1}
              helperText={companyError?.line1}
            />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.line2"
              placeholder={t('common:enter_additional_address')}
              label={`${t('common:address_line_2')} (${t(
                'common:optional'
              ).toLowerCase()})`}
              rules={maxLengthRules()}
              error={!!companyError?.line2}
              helperText={companyError?.line2}
            />
            <div />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.zip"
              placeholder={t('common:enter_postcode')}
              label={t('common:zip_postcode')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!companyError?.zip}
              helperText={companyError?.zip}
            />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.city"
              placeholder={t('common:enter_town_city')}
              label={t('common:city')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!companyError?.city}
              helperText={companyError?.city}
            />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.country"
              label={t('common:country')}
              rules={formRules}
              inputProps={{
                select: true,
                children: (
                  <>
                    <option disabled value={''}>{`${t(
                      'common:choose'
                    )}...`}</option>
                    {Object.keys(countries?.getNames('en'))?.map((v, i) => (
                      <option key={i} value={v}>
                        {countries?.getNames('en')?.[v]}
                      </option>
                    ))}
                  </>
                ),
              }}
              error={!!companyError?.country}
              helperText={companyError?.country}
            />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.eMail"
              type="email"
              placeholder={t('common:enter_email')}
              label={t('common:email')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!companyError?.eMail || !!manufacturerEmailError}
              helperText={
                manufacturerEmailError?.errorMessage || companyError?.eMail
              }
            />
            <FormPhoneInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.phone"
              placeholder={t('common:enter_phone')}
              label={t('common:phone')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!companyError?.phone}
              helperText={companyError?.phone}
              selectRef={(el) => (phoneSelectRef.current[0] = el)}
            />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.baseCompany.website"
              placeholder={t('manufacturer_account:enter_company_website')}
              label={`${t('common:website')} (${t(
                'common:optional'
              ).toLowerCase()})`}
              rules={maxLengthRules()}
              error={!!companyError?.website}
              helperText={companyError?.website}
            />
          </section>
          <h4>{t('settings:contact_person')}</h4>
          <section>
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.contactPerson"
              placeholder={t('common:enter_name')}
              label={t('common:name')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!manufacturerError?.contactPerson}
              helperText={manufacturerError?.contactPerson}
            />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.eMail"
              type="email"
              placeholder={t('common:enter_email')}
              label={t('common:email')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!manufacturerError?.eMail}
              helperText={manufacturerError?.eMail}
            />
            <FormPhoneInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.phone"
              placeholder={t('common:enter_mobile')}
              label={t('common:mobile')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!manufacturerError?.phone}
              helperText={manufacturerError?.phone}
              isMobile
              selectRef={(el) => (phoneSelectRef.current[1] = el)}
            />
          </section>
          {edit && (
            <>
              <h4>{t('service_account:account_status')}</h4>
              <FormRadio<ManufacturerAccountDetailsFormData>
                name="enabled"
                control={control}
                rules={formRules}
                radios={[
                  {
                    value: AccountStatus.ACTIVATED,
                    label: t(`common:${AccountStatus.ACTIVATED}`),
                  },
                  {
                    value: AccountStatus.DEACTIVATED,
                    label: t(`common:${AccountStatus.DEACTIVATED}`),
                  },
                ]}
                LabelRadioProps={{
                  color: 'secondary',
                  className: classes.Radio,
                }}
              />
              <p>{t('service_account:account_status_details')}</p>
            </>
          )}
          <h4>{t('service_account:other_information')}</h4>
          <p>{t('manufacturer_account:other_information')}</p>
          <section>
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.serviceAccountLimit"
              type="number"
              placeholder={t('common:enter_number')}
              label={t('manufacturer_account:service_accounts_limit')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!manufacturerError?.serviceAccountLimit}
              helperText={manufacturerError?.serviceAccountLimit}
              inputProps={{ inputProps: { min: 0 } }}
            />
            <FormInput<ManufacturerAccountDetailsFormData>
              control={control}
              name="manufacturer.appNames"
              placeholder={t('manufacturer_account:enter_app_name')}
              label={t('manufacturer_account:whitelabel_app')}
              rules={{ ...formRules, ...maxLengthRules() }}
              error={!!manufacturerError?.appNames}
              helperText={manufacturerError?.appNames}
            />
          </section>
          <FormInput<ManufacturerAccountDetailsFormData>
            control={control}
            name="manufacturer.baseCompany.notes"
            placeholder={t('manufacturer_account:add_notes')}
            label={`${t('common:notes')} (${t(
              'common:optional'
            ).toLowerCase()})`}
            inputProps={{
              fullWidth: true,
              multiline: true,
              rows: 5,
              inputProps: { maxLength: 500 },
            }}
            className={classes.Notes}
            error={!!companyError?.notes}
            helperText={`${notes?.length || 0}/500`}
          />
        </>
      ) : (
        <ManufacturerAccountDetailsFormSkeleton />
      )}
    </form>
  );
};

export default ManufacturerAccountDetailsForm;
